/**
 * Fixed-point money and quantity primitives.
 *
 * Hard rule from the spec: money is NEVER a floating point number.
 *  - Amounts are integer Paise  (1 rupee = 100 paise).
 *  - Quantities are integer Milli-units (1 unit = 1000 milli) so 1.425 kg is exactly 1425.
 *
 * Every rate is applied as: amount_paise = round_half_up(paise * milli / 1000).
 */

export type Paise = number;
export type Milli = number;

export const PAISE_PER_RUPEE = 100;
export const MILLI_PER_UNIT = 1000;

const MAX_SAFE = Number.MAX_SAFE_INTEGER;

export function assertPaise(value: number, label = 'amount'): Paise {
  if (!Number.isFinite(value) || !Number.isInteger(value)) {
    throw new Error(`${label} must be an integer number of paise, received ${value}`);
  }
  if (Math.abs(value) > MAX_SAFE / 1000) {
    throw new Error(`${label} ${value} exceeds the safe fixed-point range`);
  }
  return value;
}

export function assertMilli(value: number, label = 'quantity'): Milli {
  if (!Number.isFinite(value) || !Number.isInteger(value)) {
    throw new Error(`${label} must be an integer number of milli-units, received ${value}`);
  }
  return value;
}

/** Round half away from zero (₹1.5 -> ₹2, ₹-1.5 -> ₹-2).
 *  Named roundHalfUp for history; for positive money it is exactly half-up. */
export function roundHalfUp(value: number): number {
  if (!Number.isFinite(value)) throw new Error(`Cannot round non-finite value ${value}`);
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

/** round_half_up(paise * milli / 1000) with overflow protection. */
export function multiply(paise: Paise, milli: Milli): Paise {
  assertPaise(paise);
  assertMilli(milli);
  const product = paise * milli;
  if (Math.abs(product) > MAX_SAFE) {
    throw new Error(`Money overflow: ${paise} paise x ${milli} milli`);
  }
  return roundHalfUp(product / MILLI_PER_UNIT);
}

/** Multiply a paise amount by a plain ratio (e.g. a 12.5% discount). */
export function applyRatio(paise: Paise, numerator: number, denominator: number): Paise {
  assertPaise(paise);
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator)) {
    throw new Error(`Invalid ratio ${numerator}/${denominator}`);
  }
  if (denominator === 0) throw new Error('Ratio denominator is zero');
  const product = paise * numerator;
  if (Math.abs(product) > MAX_SAFE) throw new Error(`Money overflow in ratio ${numerator}/${denominator}`);
  return roundHalfUp(product / denominator);
}

/** Basis points: 5% = 500 bps, 2.5% = 250 bps, 1.5% = 150 bps. */
export type BasisPoints = number;

export function percentToBps(percent: number): BasisPoints {
  return roundHalfUp(percent * 100);
}

export function bpsToPercent(bps: BasisPoints): number {
  return bps / 100;
}

export function rupeesToPaise(rupees: number | string): Paise {
  let asString: string;
  if (typeof rupees === 'number') {
    if (!Number.isFinite(rupees)) throw new Error(`Invalid rupee amount "${rupees}"`);
    // Never let exponential notation ("1e+21") reach the regex: expand to plain decimal.
    // toFixed throws on absurd magnitudes, which is the correct failure there.
    asString = Math.abs(rupees) >= 1e21 ? rupees.toFixed(2) : rupees.toString();
    if (/[eE]/.test(asString)) throw new Error(`Rupee amount "${rupees}" is out of range`);
    asString = asString.trim();
  } else {
    asString = rupees.trim();
  }
  const match = /^(-)?(\d+)(?:\.(\d{1,2}))?$/.exec(asString);
  if (!match) throw new Error(`Invalid rupee amount "${asString}"`);
  const [, sign, whole, fraction = ''] = match;
  const wholePaise = Number(whole) * PAISE_PER_RUPEE;
  if (!Number.isSafeInteger(wholePaise)) throw new Error(`Rupee amount "${asString}" is out of range`);
  const paise = wholePaise + Number((fraction + '00').slice(0, 2));
  if (!Number.isSafeInteger(paise)) throw new Error(`Rupee amount "${asString}" is out of range`);
  const signed = sign ? -paise : paise;
  return assertPaise(signed);
}

/** Display-only helper: returns a binary float, so never do arithmetic on the result. */
export function paiseToRupees(paise: Paise): number {
  assertPaise(paise);
  return paise / PAISE_PER_RUPEE;
}

/**
 * Indian digit grouping: 1234567.5 -> "12,34,567.50".
 * Implemented without Intl so it behaves identically in Workers, Node and browsers.
 */
export function formatInr(paise: Paise, options: { decimals?: boolean; symbol?: boolean } = {}): string {
  const { decimals = true, symbol = true } = options;
  assertPaise(paise);
  const negative = paise < 0;
  const absolute = Math.abs(paise);
  const rupees = Math.floor(absolute / PAISE_PER_RUPEE);
  const fraction = absolute % PAISE_PER_RUPEE;
  const grouped = groupIndian(rupees);
  const body = decimals ? `${grouped}.${String(fraction).padStart(2, '0')}` : grouped;
  return `${negative ? '-' : ''}${symbol ? '₹' : ''}${body}`;
}

export function groupIndian(value: number): string {
  const digits = String(Math.abs(value));
  if (digits.length <= 3) return digits;
  const last3 = digits.slice(-3);
  const rest = digits.slice(0, -3);
  const groups = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  return `${groups},${last3}`;
}

/** Compact Indian numbering used on dashboard tiles: 12.4L, 3.2Cr. */
export function formatInrCompact(paise: Paise): string {
  const rupees = paise / PAISE_PER_RUPEE;
  const absolute = Math.abs(rupees);
  const sign = rupees < 0 ? '-' : '';
  if (absolute >= 1_00_00_000) return `${sign}₹${(absolute / 1_00_00_000).toFixed(2)}Cr`;
  if (absolute >= 1_00_000) return `${sign}₹${(absolute / 1_00_000).toFixed(2)}L`;
  if (absolute >= 1_000) return `${sign}₹${(absolute / 1_000).toFixed(1)}K`;
  return formatInr(paise, { decimals: absolute % 1 !== 0 });
}

/** "1.425 kg" from 1425 milli-units. Rounds (half away from zero) when precision < 3. */
export function formatQty(milli: Milli, unit: string, precision = 3): string {
  assertMilli(milli);
  if (!Number.isInteger(precision) || precision < 0 || precision > 3) {
    throw new Error(`formatQty precision must be 0-3, received ${precision}`);
  }
  const negative = milli < 0;
  const absolute = Math.abs(milli);
  // Round to the requested decimals first (e.g. 1425 milli at 2dp -> 1430 -> "1.43").
  const step = 10 ** (3 - precision);
  const rounded = precision >= 3 ? absolute : roundHalfUp(absolute / step) * step;
  const whole = Math.floor(rounded / MILLI_PER_UNIT);
  const fraction = String(rounded % MILLI_PER_UNIT).padStart(3, '0').slice(0, precision);
  const trimmed = fraction.replace(/0+$/, '');
  const body = trimmed.length > 0 ? `${whole}.${trimmed}` : String(whole);
  return `${negative ? '-' : ''}${body}${unit ? ` ${unit}` : ''}`;
}

/** Parse "1.425", "1,425", 1.425 -> 1425 milli-units. Float input is rounded via its decimal text, never binary multiply. */
export function qtyFromDecimal(input: number | string, precision = 3): Milli {
  // Checked for both paths: the string path used to accept precision 4 and silently drop
  // the fourth decimal, while the number path refused it.
  if (!Number.isInteger(precision) || precision < 0 || precision > 3) {
    throw new Error(`qtyFromDecimal precision must be 0-3, received ${precision}`);
  }
  if (typeof input === 'number') {
    if (!Number.isFinite(input)) throw new Error(`Invalid quantity ${input}`);
    // toFixed renders the decimal text ("1.005" stays "1.005"), avoiding 1.005*1000 = 1004.99...
    // toFixed throws on magnitudes that cannot be represented, which is the correct failure.
    return qtyFromDecimal(input.toFixed(precision), precision);
  }
  const cleaned = input.trim().replace(/,/g, '');
  const match = /^(-)?(\d+)(?:\.(\d+))?$/.exec(cleaned);
  if (!match) throw new Error(`Invalid quantity "${input}"`);
  const [, sign, whole, fraction = ''] = match;
  if (fraction.length > precision) {
    throw new Error(`Quantity "${input}" has more than ${precision} decimal places`);
  }
  // Scale to milli-units (3dp): pad the fraction to exactly 3 digits.
  const padded = (fraction + '000').slice(0, 3);
  const wholeMilli = Number(whole) * MILLI_PER_UNIT;
  if (!Number.isSafeInteger(wholeMilli)) throw new Error(`Quantity "${input}" is out of range`);
  const milli = wholeMilli + Number(padded);
  if (!Number.isSafeInteger(milli)) throw new Error(`Quantity "${input}" is out of range`);
  return sign ? -milli : milli;
}

export type RoundOffMode = 'none' | 'paisa' | 'halfRupee' | 'rupee';

/**
 * Indian retail round-off: bills are usually rounded to the nearest rupee.
 * Returns the payable total and the round-off delta (payable - raw).
 */
export function applyRoundOff(rawTotal: Paise, mode: RoundOffMode): { payable: Paise; roundOff: Paise } {
  assertPaise(rawTotal);
  if (mode !== 'none' && mode !== 'paisa' && mode !== 'halfRupee' && mode !== 'rupee') {
    throw new Error(`Unknown round-off mode "${String(mode)}"`);
  }
  switch (mode) {
    case 'none':
    case 'paisa':
      // Paise are already exact; 'paisa' is kept as an explicit alias for 'none'.
      return { payable: rawTotal, roundOff: 0 };
    case 'halfRupee': {
      const step = 50;
      const payable = roundHalfUp(rawTotal / step) * step;
      return { payable, roundOff: payable - rawTotal };
    }
    case 'rupee': {
      const step = PAISE_PER_RUPEE;
      const payable = roundHalfUp(rawTotal / step) * step;
      return { payable, roundOff: payable - rawTotal };
    }
  }
}
